import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastMessage } from '@cokitchen/cokitchen-components';

import ProcurementStatusTag from 'components/base/procurement-status-tag';
import { AddInternalProcurementType, ENUM_PROCUREMENT_STATUS } from 'models/procurement.model';

import FormStepper from 'components/base/form-stepper';
import { apiService } from 'services/base.service';
import { COKITCHEN_ID, MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import { toast } from 'react-toastify';
import UpdateRejectedProcurementDetails from './update-procurement-details';
import UpdateRejectedProcurementPreview from './update-procurement-preview';
import useKitchenProcurementContext from 'contexts/kitchen/procurement.context/context';
import RequestDetailsWrapper from 'components/base/request-details-wrapper';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

const initialProcurement = {
  ingredient_id: '',
  ingredient_name: '',
  unit_type: '',
  quantity: '',
  ingredient: null
};
const KitchenUpdateRejectedProcurement = () => {
  const [step, setStep] = useState(1);
  const [isUpdatingProcurementRequest, setIsUpdatingProcurementRequest] = useState(false);
  const { procurementToUpdate } = useKitchenProcurementContext();
  const [formData, setFormData] = useState<AddInternalProcurementType[]>(
    procurementToUpdate.items.length > 0
      ? procurementToUpdate.items.map((vendor_ingredient) => {
          return {
            ingredient_id: vendor_ingredient.ingredient_id,
            ingredient_name: vendor_ingredient.ingredient.name,
            unit_type: vendor_ingredient.ingredient.unit_type,
            quantity: vendor_ingredient.quantity,
            ingredient: vendor_ingredient.ingredient
          };
        })
      : [initialProcurement]
  );
  const { can } = useRoleContext();
  const canWriteProcurement = can(InventoryModule.KitchenRequisition, RolePermission.Write);

  const setValue = (
    index: number,
    field: keyof AddInternalProcurementType,
    value: AddInternalProcurementType[keyof AddInternalProcurementType]
  ) => {
    setFormData((prev) =>
      prev.map((data, i) => (i === index ? { ...data, [field]: value } : data))
    );
  };
  const addForm = () => setFormData([...formData, { ...initialProcurement }]);
  const removeItem = (index: number) => setFormData(formData.filter((data, i) => i !== index));
  const router = useNavigate();

  function isFormFilled(data: AddInternalProcurementType[]): boolean {
    return data.every((item) => {
      const hasIngredient = !!(item.ingredient?.id || item.ingredient_id);
      const hasQuantity = !!item.quantity;
      return hasIngredient && hasQuantity;
    });
  }

  const handleEdit = () => {
    router(`/main-store/procurement/update-details/${procurementToUpdate.code}`);
  };

  const data = {
    cokitchen_id: COKITCHEN_ID,
    title: '.',
    note: '.',
    items: formData.map((request) => {
      const ingredientId = request.ingredient?.id || request.ingredient_id;
      return {
        ingredient_id: ingredientId,
        quantity: Number(request.quantity)
      };
    })
  };

  const updateProcurementRequest = async () => {
    if (!canWriteProcurement) return;
    try {
      setIsUpdatingProcurementRequest(true);
      const response = await apiService.post('main-store/procurements', { data });
      if (response) {
        router(MAIN_STORE_ROUTE_KEYS.PROCUREMENT);
        toast(<ToastMessage message='Procurement request updated successfully' type='success' />);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error updating procurement request'}
          type='error'
        />
      );
    } finally {
      setIsUpdatingProcurementRequest(false);
    }
  };

  return (
    <RequestDetailsWrapper
      codeId={procurementToUpdate.code}
      statusTag={<ProcurementStatusTag type={ENUM_PROCUREMENT_STATUS.REJECTED} />}
      withCard={false}>
      <div className='max-w-[678px]'>
        <div className='bg-[#FFF4E5] py-2.5 px-4 w-max rounded mb-3'>
          <p className='text-[#FF9500] text-sm max-w-full'>
            Notes from admin to appear here, can be so long or so short, lorem ipsum dolum amet sit
            abel tamel lorum tarem merit tumit
          </p>
        </div>
        <FormStepper
          disabled={step === 1 ? !isFormFilled(formData) : false}
          hidePrimaryAction={!canWriteProcurement}
          step={step}
          title={step === 1 ? 'Make your selections' : 'Review selections'}
          subtitle={step === 2 ? 'Ensure you have all items needed selected' : ''}
          setStep={setStep}
          handleConfirmClick={() => {
            updateProcurementRequest();
          }}
          hideSteps
          loading={isUpdatingProcurementRequest}
          handleCancelClick={() => {
            router(-1);
          }}>
          {step === 1 ? (
            <UpdateRejectedProcurementDetails
              formData={formData}
              setValue={setValue}
              addForm={addForm}
              removeItem={removeItem}
              canWrite={canWriteProcurement}
            />
          ) : (
            <UpdateRejectedProcurementPreview formData={formData} handleEdit={handleEdit} />
          )}
        </FormStepper>
      </div>
    </RequestDetailsWrapper>
  );
};

export default KitchenUpdateRejectedProcurement;
